import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Playlists } from "../lib/api";
import { catalogKey, db } from "../lib/db";
import type { Catalog, Playlist } from "../lib/types";
import { useAuth } from "./AuthContext";

const ACTIVE_PLAYLIST_KEY = "brainzr.activePlaylist";

interface CatalogState {
  playlists: Playlist[];
  activePlaylist: Playlist | null;
  catalog: Catalog | null;
  loading: boolean;
  progress: { loaded: number; total: number } | null;
  error: string | null;
  refreshPlaylists: () => Promise<void>;
  setActivePlaylist: (id: number) => Promise<void>;
  refreshCatalog: (force?: boolean) => Promise<void>;
}

const CatalogCtx = createContext<CatalogState | null>(null);

const FRESH_MS = 1000 * 60 * 60 * 6; // 6h — anything older we re-fetch unless user forces

export function CatalogProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [activeId, setActiveId] = useState<number | null>(() => {
    const v = localStorage.getItem(ACTIVE_PLAYLIST_KEY);
    return v ? Number(v) : null;
  });
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track which playlist a catalog fetch is for so a switch doesn't get
  // coalesced into the previous playlist's in-flight promise. The bug
  // before tracking `id`: user clicks Playlist A → fetch starts → user
  // clicks Playlist B → refreshCatalog returns the A promise → A completes
  // → setCatalog(A) → activePlaylist is B but catalog is A. Forever stale
  // until the user manually triggers another load.
  const inFlight = useRef<{ id: number; promise: Promise<void> } | null>(null);
  // Track the previously-active playlist id so we can detect a *switch*
  // (vs the very first mount). On a switch we wipe `catalog` so pages
  // show their loading skeleton against a blank slate instead of briefly
  // filtering the old playlist's streams under the new playlist's name.
  const prevActiveIdRef = useRef<number | null>(null);

  const refreshPlaylists = useCallback(async () => {
    if (!user) return;
    const list = await Playlists.list();
    setPlaylists(list);
    if (list.length === 0) {
      setActiveId(null);
      localStorage.removeItem(ACTIVE_PLAYLIST_KEY);
    } else if (!activeId || !list.find((p) => p.id === activeId)) {
      const first = list[0].id;
      setActiveId(first);
      localStorage.setItem(ACTIVE_PLAYLIST_KEY, String(first));
    }
  }, [user, activeId]);

  const setActivePlaylist = useCallback(async (id: number) => {
    setActiveId(id);
    localStorage.setItem(ACTIVE_PLAYLIST_KEY, String(id));
  }, []);

  const loadCatalog = useCallback(
    async (playlistId: number, force: boolean) => {
      if (!user) return;
      const k = catalogKey(user.id, playlistId);
      setError(null);

      // 1) Hydrate from cache instantly if possible
      const cached = await db.catalogs.get(k);
      if (cached) setCatalog(cached.data);

      const stale = !cached || force || Date.now() - cached.fetchedAt > FRESH_MS;
      if (!stale) return;

      // 2) Re-fetch in background (with progress)
      setLoading(true);
      setProgress({ loaded: 0, total: 0 });
      try {
        const fresh = await Playlists.catalog(playlistId, (loaded, total) => {
          setProgress({ loaded, total });
        });
        setCatalog(fresh);
        await db.catalogs.put({ key: k, fetchedAt: Date.now(), data: fresh });
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
          (e as { message?: string })?.message ||
          "Failed to load catalog";
        setError(msg);
        if (!cached) setCatalog(null);
      } finally {
        setLoading(false);
        setProgress(null);
      }
    },
    [user]
  );

  const refreshCatalog = useCallback(
    async (force = false) => {
      if (!activeId) return;
      // Coalesce only when the in-flight fetch is for the SAME playlist;
      // a switch must always trigger a fresh load.
      if (inFlight.current && inFlight.current.id === activeId) {
        return inFlight.current.promise;
      }
      const fetchId = activeId;
      const p = loadCatalog(fetchId, force).finally(() => {
        if (inFlight.current?.id === fetchId) inFlight.current = null;
      });
      inFlight.current = { id: fetchId, promise: p };
      return p;
    },
    [activeId, loadCatalog]
  );

  // Boot: load playlists when user logs in. We deliberately DO NOT clear
  // `activeId` here: it lives in localStorage and should survive across
  // refresh / unauthed-loading-gap / logout+login-as-same-user. When the
  // playlist list comes back, refreshPlaylists() validates the stored id
  // against it and falls back to the first playlist if it no longer exists.
  useEffect(() => {
    if (user && !user.must_change_password) {
      refreshPlaylists();
    } else if (user === null) {
      setPlaylists([]);
      setCatalog(null);
    }
  }, [user, refreshPlaylists]);

  // Auto-load catalog whenever active playlist changes. On a real switch
  // (not the first hydration), clear `catalog` first so pages render a
  // clean loading skeleton instead of briefly mismatching old streams
  // under the new playlist's identity.
  useEffect(() => {
    const isSwitch =
      prevActiveIdRef.current !== null && prevActiveIdRef.current !== activeId;
    prevActiveIdRef.current = activeId;
    if (isSwitch) setCatalog(null);
    if (activeId) refreshCatalog(false);
    else setCatalog(null);
  }, [activeId, refreshCatalog]);

  const activePlaylist = useMemo(
    () => playlists.find((p) => p.id === activeId) || null,
    [playlists, activeId]
  );

  return (
    <CatalogCtx.Provider
      value={{
        playlists,
        activePlaylist,
        catalog,
        loading,
        progress,
        error,
        refreshPlaylists,
        setActivePlaylist,
        refreshCatalog,
      }}
    >
      {children}
    </CatalogCtx.Provider>
  );
}

export function useCatalog() {
  const ctx = useContext(CatalogCtx);
  if (!ctx) throw new Error("useCatalog outside provider");
  return ctx;
}
